/*
 * BygoneMark Collection Components (BMCC)
 * Component: BMCC-JS
 * Version: 1.6.2
 * Canonical ledger: /SHOPIFY_THEME_CUSTOMIZATIONS.md
 * Upgrade boundary: standalone multi-instance Finder, measured mobile disclosures, and mobile Checks accordion; no vendor-theme selectors.
 */

(function () {
  'use strict';

  if (window.BMCCFinderLoaded) return;
  window.BMCCFinderLoaded = true;

  var FILTER_KEYS = ['format', 'closure', 'color', 'budget'];
  var roots = new Set();
  var mobileIntroRoots = new Set();
  var mobileDisclosureRoots = new Set();
  var checkAccordionRoots = new Set();
  var mobileIntroQuery = window.matchMedia('(max-width: 640px)');
  var variantHoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  var VARIANT_IMAGE_CACHE_LIMIT = 32;
  var variantImageCache = new Map();
  var variantWarmObservers = new WeakMap();
  var variantWarmLoadHandlers = new WeakMap();
  var mobileIntroResizeTimer;
  var mobileDisclosureResizeTimer;
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function parameterName(key) {
    return 'bm_' + key;
  }

  function readState() {
    var params = new URLSearchParams(window.location.search);
    var state = {};
    FILTER_KEYS.forEach(function (key) {
      state[key] = params.get(parameterName(key));
    });
    return state;
  }

  function writeState(state) {
    var url = new URL(window.location.href);
    FILTER_KEYS.forEach(function (key) {
      var value = state[key];
      if (value) url.searchParams.set(parameterName(key), value);
      else url.searchParams.delete(parameterName(key));
    });
    window.history.pushState({}, '', url.toString());
  }

  function colorMatches(card, value) {
    var colors = (card.dataset.colors || '').split(/\s+/).filter(Boolean);
    return colors.indexOf(value) !== -1;
  }

  function tokenMatches(raw, value) {
    return (raw || '').split(/\s+/).filter(Boolean).indexOf(value) !== -1;
  }

  function budgetMatches(card, value, root) {
    var price = Number(card.dataset.price || 0);
    var low = Number(root.dataset.budgetLow || 0);
    var high = Number(root.dataset.budgetHigh || 0);
    if (value === 'low') return price < low;
    if (value === 'mid') return price >= low && price <= high;
    if (value === 'high') return price > high;
    return true;
  }

  function cardMatches(card, state, root) {
    if (state.format && !tokenMatches(card.dataset.format, state.format)) return false;
    if (state.closure && !tokenMatches(card.dataset.closure, state.closure)) return false;
    if (state.color && !colorMatches(card, state.color)) return false;
    if (state.budget && !budgetMatches(card, state.budget, root)) return false;
    return true;
  }

  function updateButtonStates(root, state) {
    root.querySelectorAll('[data-filter-key][data-filter-value]').forEach(function (button) {
      var isActive = state[button.dataset.filterKey] === button.dataset.filterValue;
      button.setAttribute('aria-pressed', String(isActive));
      button.classList.toggle('is-active', isActive);
    });
  }

  function updateExternalButtonStates(state) {
    document.querySelectorAll('[data-bmcc-external-filter][data-filter-key][data-filter-value]').forEach(function (button) {
      var isActive = state[button.dataset.filterKey] === button.dataset.filterValue;
      button.setAttribute('aria-pressed', String(isActive));
      button.classList.toggle('is-active', isActive);
    });
  }

  function apply(root, state, shouldWrite) {
    var shown = 0;
    root.querySelectorAll('[data-bmcc-product]').forEach(function (card) {
      var visible = cardMatches(card, state, root);
      card.hidden = !visible;
      if (visible) shown += 1;
    });

    var count = root.querySelector('[data-bmcc-count]');
    if (count) {
      var template = count.dataset.countTemplate || '{count} products';
      count.textContent = template.replace('{count}', String(shown));
    }

    var empty = root.querySelector('[data-bmcc-empty]');
    if (empty) empty.hidden = shown !== 0;

    root.bmccFinderState = state;
    updateButtonStates(root, state);
    updateExternalButtonStates(state);
    if (shouldWrite) writeState(state);
  }

  function resetState() {
    return { format: null, closure: null, color: null, budget: null };
  }

  function handleFilterClick(root, button) {
    var current = root.bmccFinderState || resetState();
    var next = Object.assign({}, current);
    var key = button.dataset.filterKey;
    var value = button.dataset.filterValue;
    next[key] = current[key] === value ? null : value;
    apply(root, next, true);
  }

  function isUnmodifiedPrimaryClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  function selectVariantSwatch(card, selectedSwatch) {
    card.querySelectorAll('[data-bmcc-variant-swatch]').forEach(function (swatch) {
      var selected = swatch === selectedSwatch;
      swatch.classList.toggle('is-selected', selected);
      if (selected) swatch.setAttribute('aria-current', 'true');
      else swatch.removeAttribute('aria-current');
    });
  }

  function getVariantPreview(swatch) {
    var card = swatch.closest('[data-bmcc-product]');
    var media = card && card.querySelector('[data-bmcc-card-media]');
    var image = media && media.querySelector('img');
    var nextSrc = swatch.dataset.bmccCardImageSrc;
    if (!card || !media || !image || !nextSrc) return null;

    return {
      swatch: swatch,
      card: card,
      media: media,
      image: image,
      nextSrc: nextSrc,
      nextSrcset: swatch.dataset.bmccCardImageSrcset || '',
      sizes: image.getAttribute('sizes') || '(max-width: 640px) 50vw, (max-width: 1000px) 40vw, 26vw',
      nextAlt: swatch.dataset.bmccCardImageAlt || image.alt,
      nextUrl: swatch.dataset.bmccVariantUrl || swatch.getAttribute('href'),
      variantId: swatch.dataset.variantId || '',
      colorName: swatch.dataset.bmccColorName || ''
    };
  }

  function variantImageCacheKey(preview) {
    return [preview.nextSrc, preview.nextSrcset, preview.sizes].join('\n');
  }

  function variantSelectionKey(preview) {
    return [variantImageCacheKey(preview), preview.nextUrl, preview.variantId].join('\n');
  }

  function touchVariantImageCache(key, entry) {
    variantImageCache.delete(key);
    variantImageCache.set(key, entry);
  }

  function pruneVariantImageCache() {
    while (variantImageCache.size > VARIANT_IMAGE_CACHE_LIMIT) {
      var oldestReadyKey;
      variantImageCache.forEach(function (entry, key) {
        if (oldestReadyKey === undefined && (entry.ready || entry.failed)) oldestReadyKey = key;
      });
      if (oldestReadyKey === undefined) return;
      variantImageCache.delete(oldestReadyKey);
    }
  }

  function createReadyVariantImageEntry(preview, key) {
    var entry = {
      key: key,
      loader: null,
      ready: true,
      failed: false,
      promise: Promise.resolve(true)
    };
    touchVariantImageCache(key, entry);
    pruneVariantImageCache();
    return entry;
  }

  function preloadVariantPreview(preview, priority) {
    var key = variantImageCacheKey(preview);
    var cached = variantImageCache.get(key);
    if (cached) {
      if (priority === 'high' && cached.loader && 'fetchPriority' in cached.loader) {
        cached.loader.fetchPriority = 'high';
      }
      touchVariantImageCache(key, cached);
      return cached;
    }

    if (
      preview.image.complete
      && preview.image.naturalWidth > 0
      && preview.image.getAttribute('src') === preview.nextSrc
      && (preview.image.getAttribute('srcset') || '') === preview.nextSrcset
    ) {
      return createReadyVariantImageEntry(preview, key);
    }

    var loader = new Image();
    loader.decoding = 'async';
    loader.sizes = preview.sizes;
    if ('fetchPriority' in loader) loader.fetchPriority = priority === 'high' ? 'high' : 'low';

    var entry = {
      key: key,
      loader: loader,
      ready: false,
      failed: false,
      promise: null
    };

    entry.promise = new Promise(function (resolve) {
      loader.addEventListener('load', function () {
        var decoded = typeof loader.decode === 'function'
          ? loader.decode().catch(function () {})
          : Promise.resolve();
        decoded.then(function () {
          entry.ready = true;
          pruneVariantImageCache();
          resolve(true);
        });
      }, { once: true });

      loader.addEventListener('error', function () {
        entry.failed = true;
        if (variantImageCache.get(key) === entry) variantImageCache.delete(key);
        resolve(false);
      }, { once: true });
    });

    touchVariantImageCache(key, entry);
    pruneVariantImageCache();
    if (preview.nextSrcset) loader.srcset = preview.nextSrcset;
    loader.src = preview.nextSrc;
    return entry;
  }

  function warmVariantCard(card) {
    if (!variantHoverQuery.matches || !card) return;
    card.querySelectorAll('[data-bmcc-variant-swatch]').forEach(function (swatch) {
      var preview = getVariantPreview(swatch);
      if (preview) preloadVariantPreview(preview, 'low');
    });
  }

  function scheduleVariantCardWarm(card) {
    if (!variantHoverQuery.matches || !card || card.dataset.bmccVariantWarmScheduled === 'true') return;
    card.dataset.bmccVariantWarmScheduled = 'true';
    var ownerRoot = card.closest('[data-bmcc-finder]');
    var run = function () {
      delete card.dataset.bmccVariantWarmScheduled;
      if (ownerRoot && roots.has(ownerRoot) && document.contains(card)) warmVariantCard(card);
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1000 });
    } else {
      window.setTimeout(run, 120);
    }
  }

  function stopVariantWarmup(root) {
    var observer = variantWarmObservers.get(root);
    if (observer) observer.disconnect();
    variantWarmObservers.delete(root);

    var loadHandler = variantWarmLoadHandlers.get(root);
    if (loadHandler) window.removeEventListener('load', loadHandler);
    variantWarmLoadHandlers.delete(root);
  }

  function startVariantWarmup(root) {
    if (!root || !document.contains(root) || !variantHoverQuery.matches || variantWarmObservers.has(root)) return;

    var observeCards = function () {
      variantWarmLoadHandlers.delete(root);
      if (!document.contains(root) || !variantHoverQuery.matches || variantWarmObservers.has(root)) return;

      if (!('IntersectionObserver' in window)) {
        return;
      }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          scheduleVariantCardWarm(entry.target);
        });
      }, { rootMargin: '160px 0px', threshold: 0.01 });

      variantWarmObservers.set(root, observer);
      root.querySelectorAll('[data-bmcc-product]').forEach(function (card) {
        observer.observe(card);
      });
    };

    if (document.readyState === 'complete') {
      observeCards();
    } else {
      variantWarmLoadHandlers.set(root, observeCards);
      window.addEventListener('load', observeCards, { once: true });
    }
  }

  function syncVariantWarmups() {
    roots.forEach(function (root) {
      if (!document.contains(root)) {
        stopVariantWarmup(root);
        roots.delete(root);
      } else if (variantHoverQuery.matches) {
        startVariantWarmup(root);
      } else {
        stopVariantWarmup(root);
      }
    });
  }

  function syncVariantSelection(preview) {
    selectVariantSwatch(preview.card, preview.swatch);
    if (preview.nextUrl) {
      preview.card.querySelectorAll('[data-bmcc-card-link]').forEach(function (link) {
        link.setAttribute('href', preview.nextUrl);
      });
    }
  }

  function commitVariantPreview(preview, requestId, selectionKey) {
    var card = preview.card;
    if (card.dataset.bmccImageRequest !== requestId) return false;

    if (preview.nextSrcset) preview.image.setAttribute('srcset', preview.nextSrcset);
    else preview.image.removeAttribute('srcset');
    preview.image.setAttribute('src', preview.nextSrc);
    preview.image.alt = preview.nextAlt;
    preview.image.loading = 'eager';
    card.dataset.bmccCurrentVariantKey = selectionKey;
    delete card.dataset.bmccPendingVariantKey;
    preview.media.classList.remove('is-loading');
    preview.media.removeAttribute('aria-busy');

    var status = card.querySelector('[data-bmcc-variant-status]');
    var title = card.querySelector('.bmcc-finder-card__title-link');
    if (status) {
      var finderRoot = card.closest('[data-bmcc-finder]');
      var statusTemplate = finderRoot && finderRoot.dataset.bmccVariantStatusTemplate
        ? finderRoot.dataset.bmccVariantStatusTemplate
        : 'Showing {product} in {color}.';
      status.textContent = statusTemplate
        .replace('{product}', title ? title.textContent.trim() : 'product')
        .replace('{color}', preview.colorName || '');
    }
    return true;
  }

  function activateVariantPreview(preview) {
    var swatch = preview.swatch;
    var card = preview.card;
    var selectionKey = variantSelectionKey(preview);

    syncVariantSelection(preview);

    if (
      card.dataset.bmccCurrentVariantKey === selectionKey
      && swatch.getAttribute('aria-current') === 'true'
      && preview.image.getAttribute('src') === preview.nextSrc
    ) {
      return true;
    }

    if (card.dataset.bmccPendingVariantKey === selectionKey) return true;

    var requestId = String(Number(card.dataset.bmccImageRequest || 0) + 1);
    card.dataset.bmccImageRequest = requestId;
    card.dataset.bmccPendingVariantKey = selectionKey;

    var entry = preloadVariantPreview(preview, 'high');
    if (entry.ready) return commitVariantPreview(preview, requestId, selectionKey);

    preview.media.setAttribute('aria-busy', 'true');
    entry.promise.then(function (loaded) {
      if (card.dataset.bmccImageRequest !== requestId) return;
      if (!loaded) {
        delete card.dataset.bmccPendingVariantKey;
        preview.media.classList.remove('is-loading');
        preview.media.removeAttribute('aria-busy');
        return;
      }
      commitVariantPreview(preview, requestId, selectionKey);
    });
    return true;
  }

  function handleVariantSwatchClick(event, swatch) {
    if (!isUnmodifiedPrimaryClick(event)) return false;

    var preview = getVariantPreview(swatch);
    if (!preview) return false;

    event.preventDefault();
    return activateVariantPreview(preview);
  }

  function handleVariantSwatchHover(event, swatch) {
    if (event.pointerType === 'touch') return false;
    if (!variantHoverQuery.matches) return false;
    if (event.relatedTarget && swatch.contains(event.relatedTarget)) return false;

    var preview = getVariantPreview(swatch);
    if (!preview) return false;

    return activateVariantPreview(preview);
  }

  function initFinder(root) {
    if (!root || root.dataset.bmccInitialized === 'true') return;
    root.dataset.bmccInitialized = 'true';
    roots.add(root);
    startVariantWarmup(root);

    root.addEventListener('click', function (event) {
      var variantSwatch = event.target.closest('[data-bmcc-variant-swatch]');
      if (variantSwatch && root.contains(variantSwatch) && handleVariantSwatchClick(event, variantSwatch)) {
        return;
      }

      var filterButton = event.target.closest('[data-filter-key][data-filter-value]');
      if (filterButton && root.contains(filterButton)) {
        handleFilterClick(root, filterButton);
        return;
      }

      var resetButton = event.target.closest('[data-bmcc-reset]');
      if (resetButton && root.contains(resetButton)) {
        apply(root, resetState(), true);
        return;
      }

      var toggle = event.target.closest('[data-bmcc-toggle-filters]');
      if (toggle && root.contains(toggle)) {
        var panel = root.querySelector('[data-bmcc-filter-panel]');
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.classList.toggle('is-open', !expanded);
      }
    });

    root.addEventListener('pointerover', function (event) {
      var card = event.target.closest('[data-bmcc-product]');
      if (
        variantHoverQuery.matches
        && event.pointerType !== 'touch'
        && card
        && root.contains(card)
        && (!event.relatedTarget || !card.contains(event.relatedTarget))
      ) {
        warmVariantCard(card);
      }

      var variantSwatch = event.target.closest('[data-bmcc-variant-swatch]');
      if (!variantSwatch || !root.contains(variantSwatch)) return;
      handleVariantSwatchHover(event, variantSwatch);
    });

    root.addEventListener('focusin', function (event) {
      var variantSwatch = event.target.closest('[data-bmcc-variant-swatch]');
      if (!variantSwatch || !root.contains(variantSwatch) || !variantHoverQuery.matches) return;
      warmVariantCard(variantSwatch.closest('[data-bmcc-product]'));
    });

    apply(root, readState(), false);
    root.classList.add('is-finder-enhanced');
  }

  function restoreMobileIntroFocus(root) {
    root.querySelectorAll('[data-bmcc-saved-tabindex]').forEach(function (element) {
      var previous = element.dataset.bmccSavedTabindex;
      if (previous === '__none__') element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', previous);
      delete element.dataset.bmccSavedTabindex;
    });
  }

  function disableClippedMobileIntroFocus(root, content) {
    restoreMobileIntroFocus(root);
    var visibleBottom = content.getBoundingClientRect().bottom + 1;
    content.querySelectorAll(FOCUSABLE_SELECTOR).forEach(function (element) {
      var rect = element.getBoundingClientRect();
      if (rect.bottom <= visibleBottom) return;
      element.dataset.bmccSavedTabindex = element.hasAttribute('tabindex')
        ? element.getAttribute('tabindex')
        : '__none__';
      element.setAttribute('tabindex', '-1');
    });
  }

  function syncMobileIntro(root) {
    if (!root || !document.contains(root)) return;

    var content = root.querySelector('[data-bmcc-mobile-intro-content]');
    var toggle = root.querySelector('[data-bmcc-mobile-intro-toggle]');
    if (!content || !toggle) return;

    restoreMobileIntroFocus(root);
    root.classList.remove('is-enhanced', 'is-collapsed', 'is-expanded');
    root.style.removeProperty('--bmcc-intro-collapsed-height');
    toggle.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');

    if (!mobileIntroQuery.matches) {
      root.dataset.bmccIntroExpanded = 'false';
      return;
    }

    var previewLines = Number(root.dataset.previewLines || 4);
    previewLines = Math.min(8, Math.max(3, previewLines));

    var computed = window.getComputedStyle(content);
    var lineHeight = Number.parseFloat(computed.lineHeight);
    if (!Number.isFinite(lineHeight)) {
      lineHeight = Number.parseFloat(computed.fontSize) * 1.7;
    }

    var collapsedHeight = Math.ceil(lineHeight * previewLines);
    var fullHeight = content.scrollHeight;
    if (fullHeight <= collapsedHeight + 2) return;

    root.style.setProperty('--bmcc-intro-collapsed-height', collapsedHeight + 'px');
    root.classList.add('is-enhanced');
    toggle.hidden = false;

    var expanded = root.dataset.bmccIntroExpanded === 'true';
    toggle.setAttribute('aria-expanded', String(expanded));
    if (expanded) {
      root.classList.add('is-expanded');
      return;
    }

    root.classList.add('is-collapsed');
    disableClippedMobileIntroFocus(root, content);
  }

  function initMobileIntro(root) {
    if (!root || root.dataset.bmccMobileIntroInitialized === 'true') return;
    root.dataset.bmccMobileIntroInitialized = 'true';
    root.dataset.bmccIntroExpanded = 'false';
    mobileIntroRoots.add(root);

    var toggle = root.querySelector('[data-bmcc-mobile-intro-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        root.dataset.bmccIntroExpanded = String(root.dataset.bmccIntroExpanded !== 'true');
        syncMobileIntro(root);
      });
    }

    syncMobileIntro(root);
  }

  function syncAllMobileIntros() {
    mobileIntroRoots.forEach(function (root) {
      if (document.contains(root)) syncMobileIntro(root);
      else mobileIntroRoots.delete(root);
    });
  }

  function scheduleMobileIntroSync() {
    window.clearTimeout(mobileIntroResizeTimer);
    mobileIntroResizeTimer = window.setTimeout(syncAllMobileIntros, 80);
  }

  function syncMobileDisclosure(root) {
    if (!root || !document.contains(root)) return;

    var content = root.querySelector('[data-bmcc-mobile-disclosure-content]');
    var toggle = root.querySelector('[data-bmcc-mobile-disclosure-toggle]');
    if (!content || !toggle) return;

    restoreMobileIntroFocus(root);
    root.classList.remove('is-enhanced', 'is-collapsed', 'is-expanded');
    root.style.removeProperty('--bmcc-mobile-disclosure-collapsed-height');
    toggle.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');

    if (!mobileIntroQuery.matches) {
      root.dataset.bmccDisclosureExpanded = 'false';
      return;
    }

    var previewLines = Number(root.dataset.previewLines || 6);
    previewLines = Math.min(8, Math.max(3, previewLines));

    var computed = window.getComputedStyle(content);
    var lineHeight = Number.parseFloat(computed.lineHeight);
    if (!Number.isFinite(lineHeight)) {
      lineHeight = Number.parseFloat(computed.fontSize) * 1.7;
    }

    var collapsedHeight = Math.ceil(lineHeight * previewLines);
    var fullHeight = content.scrollHeight;
    if (fullHeight <= collapsedHeight + 2) return;

    root.style.setProperty('--bmcc-mobile-disclosure-collapsed-height', collapsedHeight + 'px');
    root.classList.add('is-enhanced');
    toggle.hidden = false;

    var expanded = root.dataset.bmccDisclosureExpanded === 'true';
    toggle.setAttribute('aria-expanded', String(expanded));
    if (expanded) {
      root.classList.add('is-expanded');
      return;
    }

    root.classList.add('is-collapsed');
    disableClippedMobileIntroFocus(root, content);
  }

  function initMobileDisclosure(root) {
    if (!root || root.dataset.bmccMobileDisclosureInitialized === 'true') return;
    root.dataset.bmccMobileDisclosureInitialized = 'true';
    root.dataset.bmccDisclosureExpanded = 'false';
    mobileDisclosureRoots.add(root);

    var toggle = root.querySelector('[data-bmcc-mobile-disclosure-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        root.dataset.bmccDisclosureExpanded = String(root.dataset.bmccDisclosureExpanded !== 'true');
        syncMobileDisclosure(root);
      });
    }

    syncMobileDisclosure(root);
  }

  function syncAllMobileDisclosures() {
    mobileDisclosureRoots.forEach(function (root) {
      if (document.contains(root)) syncMobileDisclosure(root);
      else mobileDisclosureRoots.delete(root);
    });
  }

  function scheduleMobileDisclosureSync() {
    window.clearTimeout(mobileDisclosureResizeTimer);
    mobileDisclosureResizeTimer = window.setTimeout(syncAllMobileDisclosures, 80);
  }

  function setCheckExpanded(card, expanded) {
    var toggle = card.querySelector('[data-bmcc-check-toggle]');
    var content = card.querySelector('[data-bmcc-check-content]');
    if (!toggle || !content) return;

    card.dataset.bmccCheckExpanded = String(expanded);
    card.classList.toggle('is-check-expanded', expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    content.hidden = !expanded;
  }

  function syncCheckAccordion(root) {
    if (!root || !document.contains(root)) return;

    var isMobile = mobileIntroQuery.matches;
    root.classList.toggle('is-check-accordion-enhanced', isMobile);
    root.querySelectorAll('[data-bmcc-check-item]').forEach(function (card) {
      var toggle = card.querySelector('[data-bmcc-check-toggle]');
      var content = card.querySelector('[data-bmcc-check-content]');
      if (!toggle || !content) return;

      if (!isMobile) {
        card.dataset.bmccCheckExpanded = 'false';
        card.classList.remove('is-check-enhanced', 'is-check-expanded');
        toggle.hidden = true;
        toggle.setAttribute('aria-expanded', 'true');
        content.hidden = false;
        return;
      }

      card.classList.add('is-check-enhanced');
      toggle.hidden = false;
      setCheckExpanded(card, card.dataset.bmccCheckExpanded === 'true');
    });
  }

  function initCheckAccordion(root) {
    if (!root || root.dataset.bmccCheckAccordionInitialized === 'true') return;
    root.dataset.bmccCheckAccordionInitialized = 'true';
    checkAccordionRoots.add(root);

    root.querySelectorAll('[data-bmcc-check-item]').forEach(function (card) {
      card.dataset.bmccCheckExpanded = 'false';
    });

    root.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-bmcc-check-toggle]');
      if (!toggle || !root.contains(toggle) || !mobileIntroQuery.matches) return;

      var card = toggle.closest('[data-bmcc-check-item]');
      var shouldExpand = card.dataset.bmccCheckExpanded !== 'true';
      root.querySelectorAll('[data-bmcc-check-item]').forEach(function (item) {
        if (item !== card) setCheckExpanded(item, false);
      });
      setCheckExpanded(card, shouldExpand);
    });

    syncCheckAccordion(root);
  }

  function syncAllCheckAccordions() {
    checkAccordionRoots.forEach(function (root) {
      if (document.contains(root)) syncCheckAccordion(root);
      else checkAccordionRoots.delete(root);
    });
  }

  function initAll(scope) {
    var context = scope || document;
    if (context.matches && context.matches('[data-bmcc-finder]')) initFinder(context);
    if (context.matches && context.matches('[data-bmcc-mobile-intro]')) initMobileIntro(context);
    if (context.matches && context.matches('[data-bmcc-mobile-disclosure]')) initMobileDisclosure(context);
    if (context.matches && context.matches('[data-bmcc-check-accordion]')) initCheckAccordion(context);
    context.querySelectorAll('[data-bmcc-finder]').forEach(initFinder);
    context.querySelectorAll('[data-bmcc-mobile-intro]').forEach(initMobileIntro);
    context.querySelectorAll('[data-bmcc-mobile-disclosure]').forEach(initMobileDisclosure);
    context.querySelectorAll('[data-bmcc-check-accordion]').forEach(initCheckAccordion);
  }

  document.addEventListener('click', function (event) {
    var external = event.target.closest('[data-bmcc-external-filter][data-filter-key][data-filter-value]');
    if (!external) return;

    var targetSelector = external.dataset.bmccFilterTarget;
    var root = targetSelector ? document.querySelector(targetSelector) : document.querySelector('[data-bmcc-finder]');
    if (!root) return;

    handleFilterClick(root, external);
    root.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  });

  window.addEventListener('popstate', function () {
    var state = readState();
    roots.forEach(function (root) {
      if (document.contains(root)) apply(root, Object.assign({}, state), false);
      else roots.delete(root);
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.matches && event.target.matches('[data-bmcc-finder]')) {
      stopVariantWarmup(event.target);
      roots.delete(event.target);
    }
    event.target.querySelectorAll('[data-bmcc-finder]').forEach(function (root) {
      stopVariantWarmup(root);
      roots.delete(root);
    });
    if (event.target.matches && event.target.matches('[data-bmcc-mobile-intro]')) {
      restoreMobileIntroFocus(event.target);
      mobileIntroRoots.delete(event.target);
    }
    event.target.querySelectorAll('[data-bmcc-mobile-intro]').forEach(function (root) {
      restoreMobileIntroFocus(root);
      mobileIntroRoots.delete(root);
    });
    if (event.target.matches && event.target.matches('[data-bmcc-mobile-disclosure]')) {
      restoreMobileIntroFocus(event.target);
      mobileDisclosureRoots.delete(event.target);
    }
    event.target.querySelectorAll('[data-bmcc-mobile-disclosure]').forEach(function (root) {
      restoreMobileIntroFocus(root);
      mobileDisclosureRoots.delete(root);
    });
    if (event.target.matches && event.target.matches('[data-bmcc-check-accordion]')) {
      checkAccordionRoots.delete(event.target);
    }
    event.target.querySelectorAll('[data-bmcc-check-accordion]').forEach(function (root) {
      checkAccordionRoots.delete(root);
    });
  });

  window.addEventListener('resize', scheduleMobileIntroSync);
  window.addEventListener('resize', scheduleMobileDisclosureSync);
  if (mobileIntroQuery.addEventListener) {
    mobileIntroQuery.addEventListener('change', scheduleMobileIntroSync);
    mobileIntroQuery.addEventListener('change', scheduleMobileDisclosureSync);
    mobileIntroQuery.addEventListener('change', syncAllCheckAccordions);
  } else if (mobileIntroQuery.addListener) {
    mobileIntroQuery.addListener(scheduleMobileIntroSync);
    mobileIntroQuery.addListener(scheduleMobileDisclosureSync);
    mobileIntroQuery.addListener(syncAllCheckAccordions);
  }

  if (variantHoverQuery.addEventListener) {
    variantHoverQuery.addEventListener('change', syncVariantWarmups);
  } else if (variantHoverQuery.addListener) {
    variantHoverQuery.addListener(syncVariantWarmups);
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleMobileIntroSync);
    document.fonts.ready.then(scheduleMobileDisclosureSync);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
})();
